import { defineStore } from "pinia";
import { BigNumber } from "ethers";
import { Auction, AuctionUpdate, CardType, NftEquipment, NftSkill } from "fungi-types";
import { ASSETS_URL } from "~/application/config";
import { auctionApi } from "~/api/auction";
import { computed, ref } from "vue";
import { FlashSaleGetResponse } from "~/api/auction/types";

const setItem = (key: string, value: string) => localStorage.setItem(key, value);
const getItem = (key: string) => localStorage.getItem(key) || "";

export const useStoreFlashSale = defineStore("flash-sale", () => {
  const backgroundImg = ref<string>("");
  const bannerImg = ref<string>("");
  const lastHiddenFlashSaleId = ref<string | null>(getItem("hideFlashSaleId") || null);
  const loading = ref<boolean>(false);
  const flashSale = ref<FlashSaleGetResponse>();
  const auctions = computed(() => {
    if (!flashSale.value) return [];
    return flashSale.value.auctions;
  });

  const hideFlashSaleBanner = () => {
    const flashSaleId = flashSale.value?.cuid;
    if (!flashSaleId) return;
    lastHiddenFlashSaleId.value = flashSaleId;
    setItem("hideFlashSaleId", flashSaleId);
  };

  const flashSaleId = computed(() => flashSale.value?.cuid || "");

  const showFlashSaleBanner = computed(() => {
    if (!flashSaleId.value) return false;
    return lastHiddenFlashSaleId.value !== flashSale.value?.cuid;
  });

  const setBackgroundImg = (cuid: string, file: "banner" | "background"): string => {
    if (!flashSale) return "";
    return `url("${ASSETS_URL}/flashsale/${cuid}/${file}.webp")`;
  };

  const updateAuction = (
    data: AuctionUpdate,
    auctionsSrc?: (NftSkill | NftEquipment)[]
  ): Promise<{
    prevBidder: { username: string | undefined; cuid: string | undefined } | null;
    item: NftSkill | NftEquipment;
    index: number;
  } | null> => {
    console.log("updateAuction", data);
    const nfts = auctionsSrc || auctions.value;

    // Find the auction to update
    const index = nfts.findIndex((nft) => nft.auction?.cuid === data.auction.cuid);

    if (index === -1) return null;
    const nft = nfts[index];
    if (!nft.auction) {
      console.warn("No auction found for the given cuid:", data.auction.cuid);
      return null;
    }

    // Validate that new bid is after and has a higher amount than the last known bid
    const newBidAmount = BigNumber.from(data.auction.lastBidAmount);
    const lastBidAmount = BigNumber.from(nft.auction?.lastBidAmount ?? "0");
    const newBidAt = new Date(data.auction.lastBidAt);
    const lastBidAt = new Date(nft.auction?.lastBidAt ?? "");
    const isNewBidBeforeLastKnownBid = newBidAt.getTime() < lastBidAt.getTime();
    const isNewBidLesserThanLastKnownBid = newBidAmount.lt(lastBidAmount);
    if (isNewBidBeforeLastKnownBid || isNewBidLesserThanLastKnownBid) return null;

    // Get the last known bidder info before overwriting the auction's data
    const prevBidder = {
      username: nft.auction?.lastBidder?.username,
      cuid: nft.auction?.lastBidder?.cuid,
    };

    const updatedAuction: Auction = {
      ...nft.auction,
      lastBidder: {
        username: data.bidder.username,
        cuid: data.bidder.cuid,
      },
      lastBidAt: data.auction.lastBidAt as string,
      endedAt: data.auction.endedAt as string,
      lastBidAmount: data.auction.lastBidAmount,
      countOrders: data.auction.countOrders,
      status: data.auction.status,
    };

    nfts[index] = { ...nft, auction: { ...updatedAuction } };

    return { prevBidder, item: nfts[index], index };
  };

  const recentActivity = computed(() => {
    if (!flashSale.value) return [];
    const validAuctions = flashSale.value.auctions.filter(
      (item) => item.auction && item.auction.lastBidAt && item.auction.lastBidAt !== null
    );
    validAuctions.sort((a, b) => {
      const aDate = a.auction?.lastBidAt ?? "";
      const bDate = b.auction?.lastBidAt ?? "";
      if (bDate && aDate) {
        return bDate.localeCompare(aDate);
      } else if (bDate) {
        return -1;
      } else if (aDate) {
        return 1;
      }
      return 0;
    });
    return validAuctions.slice(0, 8);
  });

  const getFlashSale = async (identifier?: string) => {
    try {
      loading.value = true;
      flashSale.value = await auctionApi.flashSales.get.query(
        identifier ? { cuid: identifier } : undefined
      );
      if (flashSale.value) {
        backgroundImg.value = setBackgroundImg(flashSale.value.cuid, "background");
        bannerImg.value = setBackgroundImg(flashSale.value.cuid, "banner");
      }
    } catch (e: any) {
    } finally {
      loading.value = false;
    }
  };

  const summary = computed(() => {
    const data: {
      highest: NftEquipment | NftSkill | null;
      lowest: NftEquipment | NftSkill | null;
      mostOrders: NftEquipment | NftSkill | null;
    } = { highest: null, lowest: null, mostOrders: null };

    data.highest = auctions.value.reduce(
      (acc, auction) => {
        if (
          !acc ||
          BigNumber.from(auction.auction?.lastBidAmount ?? "0").gt(
            BigNumber.from(acc.auction?.lastBidAmount ?? "0")
          )
        ) {
          return auction;
        }
        return acc;
      },
      null as NftEquipment | NftSkill | null
    );
    data.lowest = auctions.value.reduce(
      (acc, auction) => {
        if (
          !acc ||
          (BigNumber.from(auction.auction?.lastBidAmount ?? "0").lt(
            BigNumber.from(acc.auction?.lastBidAmount ?? "0")
          ) &&
            auction.auction?.lastBidAmount)
        ) {
          return auction;
        }
        return acc;
      },
      null as NftEquipment | NftSkill | null
    );
    data.mostOrders = auctions.value.reduce(
      (acc, auction) => {
        if (!acc || (auction?.auction?.countOrders || 0) > (acc?.auction?.countOrders || 0))
          return auction;
        return acc;
      },
      null as NftEquipment | NftSkill | null
    );
    return data;
  });

  return {
    loading,
    flashSale,
    backgroundImg,
    bannerImg,
    auctions,

    getFlashSale,
    updateAuction,
    hideFlashSaleBanner,
    setBackgroundImg,
    showFlashSaleBanner,
    recentActivity,
    summary,
  };
});
